import { useState } from 'react';
import { Image, Link, Save, Tag } from 'lucide-react';
import { Button, Input, SettingsSection } from '@/design-system';
import { useAuth } from '@/core/auth';
import { useBrandingSettings } from '../../hooks/useBrandingSettings';
import { BrandingAssetField } from '../BrandingAssetField';

type Draft = { platformName: string; supportUrl: string; privacyPolicyUrl: string; termsOfServiceUrl: string };
export function BrandingSection() {
  const { query, patch, upload } = useBrandingSettings();
  const { user } = useAuth();
  const [draft, setDraft] = useState<Partial<Draft>>({});
  const [validation, setValidation] = useState('');
  const canEdit = !!user;
  const busy = patch.isPending || upload.isPending;
  const data = query.data?.public;
  const values: Draft = {
    platformName: draft.platformName ?? data?.platformName ?? '',
    supportUrl: draft.supportUrl ?? data?.supportUrl ?? '',
    privacyPolicyUrl: draft.privacyPolicyUrl ?? data?.privacyPolicyUrl ?? '',
    termsOfServiceUrl: draft.termsOfServiceUrl ?? data?.termsOfServiceUrl ?? '',
  };
  const change = (field: keyof Draft, value: string) => { setDraft((prev) => ({ ...prev, [field]: value })); setValidation(''); patch.reset(); upload.reset(); };
  const save = () => {
    if (!values.platformName.trim()) { setValidation('Nome da plataforma é obrigatório.'); return; }
    for (const key of ['supportUrl', 'privacyPolicyUrl', 'termsOfServiceUrl'] as const) {
      if (!values[key].trim()) continue;
      try { if (new URL(values[key].trim()).protocol !== 'https:') throw new Error(); }
      catch { setValidation('Use URLs válidas iniciadas com https:// nos links.'); return; }
    }
    patch.mutate({
      platformName: values.platformName.trim(),
      supportUrl: values.supportUrl.trim() || null,
      privacyPolicyUrl: values.privacyPolicyUrl.trim() || null,
      termsOfServiceUrl: values.termsOfServiceUrl.trim() || null,
    }, { onSuccess: () => setDraft({}) });
  };
  if (query.isPending) return <p role="status" className="text-sm text-gray-500">Carregando aparência…</p>;
  if (!data) return <div role="alert" className="space-y-3 text-sm text-red-700">Não foi possível carregar a aparência.
    <Button variant="subtle" onClick={() => void query.refetch()}>Tentar novamente</Button></div>;
  return <div className="space-y-5">
    {(validation || query.isError || patch.isError || upload.isError) && <p role="alert" className="text-sm text-red-700">{validation || 'Não foi possível concluir a operação. Seus dados foram preservados; tente novamente.'}</p>}
    {(patch.isSuccess || upload.isSuccess) && <p role="status" className="text-sm text-emerald-700">Aparência atualizada.</p>}
    <SettingsSection title="Identidade" icon={Tag} description="Nome exibido na aba do navegador e nos textos da interface.">
      <Input label="Nome da plataforma" value={values.platformName} disabled={!canEdit || busy} onChange={(event) => change('platformName', event.target.value)} />
    </SettingsSection>
    <SettingsSection title="URLs legais e suporte" icon={Link} description="Links disponíveis na plataforma. Use apenas URLs https://.">
      <div className="space-y-4">
        <Input label="Suporte" value={values.supportUrl} placeholder="https://…" disabled={!canEdit || busy} onChange={(event) => change('supportUrl', event.target.value)} />
        <Input label="Política de privacidade" value={values.privacyPolicyUrl} placeholder="https://…" disabled={!canEdit || busy} onChange={(event) => change('privacyPolicyUrl', event.target.value)} />
        <Input label="Termos de utilização" value={values.termsOfServiceUrl} placeholder="https://…" disabled={!canEdit || busy} onChange={(event) => change('termsOfServiceUrl', event.target.value)} />
      </div>
    </SettingsSection>
    <SettingsSection title="Logos e ícones" icon={Image} description="PNG, JPEG ou WebP. Imagens armazenadas publicamente.">
      <div className="divide-y divide-gray-100">
        {([
          { type: 'logo', field: 'logoUrl', title: 'Logo (fundo claro)', description: 'Aplicado em fundos claros, como a tela de login.', url: data.logoUrl },
          { type: 'logoDark', field: 'logoUrlDark', title: 'Logo (fundo escuro)', description: 'Aplicado na barra lateral escura. Opcional.', url: data.logoUrlDark, dark: true },
          { type: 'favicon', field: 'faviconUrl', title: 'Favicon', description: 'Ícone da aba do navegador. Recomendado: 32×32 ou 64×64.', url: data.faviconUrl, favicon: true },
        ] as const).map(({ type, field, ...asset }) => <BrandingAssetField key={type} {...asset} disabled={!canEdit || busy}
          onUpload={(file) => { patch.reset(); upload.mutate({ file, type }); }}
          onRemove={() => { upload.reset(); patch.mutate({ [field]: null }); }} />)}
      </div>
      <p className="mt-4 text-xs text-gray-500">Envios e remoções de imagens são aplicados imediatamente.</p>
    </SettingsSection>
    <div className="flex justify-end gap-2">
      <Button variant="ghost" disabled={busy || query.isFetching} onClick={() => {
        if (Object.keys(draft).length) { setValidation('Salve as alterações antes de recarregar.'); return; }
        void query.refetch();
      }}>Recarregar</Button>
      <Button disabled={!canEdit || busy || !Object.keys(draft).length} isLoading={patch.isPending} onClick={save}><Save size={16} />Salvar alterações</Button>
    </div>
  </div>;
}
